// therule.tsx — the RESTRUCTURED home, ported from the Claude Design delivery "THE RULE"
// (project 337c14f7). This file is the a3 answer: the app's bones, not its paint.
//
// What changed and WHY (a3 VERDICT was FAIL — see DESIGN.md):
//   R1  the daily streak hero is DELETED. No competitor has one, and climbing contradicts it —
//       2–4 sessions a week, and projecting means failing the same problem for weeks. The home
//       now leads with the ASCENT PYRAMID (KAYA: "build your ascent pyramid"; 8a.nu's scorecard
//       is one) over a ROLLING 12 MONTHS (8a.nu scores best-10-of-last-12-months).
//   R2  the app's own verb leads: the LOG RAIL is pinned to the bottom — tap a grade band and the
//       sheet opens with that grade already set. "+ Log climb" under a tab switcher is gone.
//   R3  the reverse-chronological card list is demoted and re-cut into SESSIONS.
//   R4  ascent style (onsight/flash/redpoint/repeat/attempt) is a drawn mark, first-class.
//   R5  projects are a real surface with a SESSION LADDER — tally strokes grouped per session.
//   R6  no serif app-name header, no .hero block, no "Recent <noun>" h2, no uniform card list —
//       the four things every sibling (Mood/Stash/Habits) also had.
import React, { useMemo } from "react";
import type { Climb, SeasonSignals } from "./store";
import { AscentMark, EnvMark, type MarkId } from "./marks";

const DAY = 86400000;
const daysAgo = (iso: string) => Math.floor((Date.now() - new Date(iso + "T00:00:00").getTime()) / DAY);
const within = (c: Climb, days: number) => daysAgo(c.climbed_on) <= days;

/** grade.rs axis: V0..V17 → 100 + n*5. Five weights; the numeral stands on a graduation
 *  whose weight rises with the grade. One system across V-scale, French and YDS because
 *  they all reduce to the same 100–185 axis. */
export function gradeTier(gv: number): string {
  if (gv < 0) return "t2";              // web build cannot parse grades (grade_value = -1)
  if (gv <= 114) return "t1";
  if (gv <= 129) return "t2";
  if (gv <= 144) return "t3";
  if (gv <= 164) return "t4";
  return "t5";
}

export function GradeToken({ grade, gv, apex, size, onClick, title }: {
  grade: string; gv: number; apex?: boolean; size?: "sm" | "lg";
  onClick?: () => void; title?: string;
}) {
  const cls = ["gt", gradeTier(gv), apex ? "apex" : "", size ? `gt-${size}` : ""].filter(Boolean).join(" ");
  if (!onClick) return <span className={cls} title={title}>{grade || "—"}</span>;
  return (
    <button type="button" className={cls} onClick={onClick} title={title}
            aria-label={title ?? `Log a ${grade}`}>{grade || "—"}</button>
  );
}

const isRope = (d: string) => d === "sport" || d === "trad" || d === "toprope";

/** THE READOUT — replaces the streak hero. The hardest band actually SENT in a rolling
 *  12 months, plus the three counts that describe the season. */
export function TopBand({ climbs }: { climbs: Climb[] }) {
  const r = useMemo(() => {
    const year = climbs.filter((c) => within(c, 365));
    const sends = year.filter((c) => c.sent);
    const top = sends.reduce<Climb | null>((a, c) => (!a || c.grade_value > a.grade_value ? c : a), null);
    const goes = year.reduce((n, c) => n + Math.max(1, c.attempts || 1), 0);
    return { top, sends: sends.length, goes, days: new Set(year.map((c) => c.climbed_on)).size };
  }, [climbs]);

  return (
    <div className="hero">
      <div style={{ minWidth: 0 }}>
        <div className="lbl" style={{ marginBottom: 6 }}>Top band · last 12 months</div>
        <div className="row" style={{ gap: 10, alignItems: "flex-end", flexWrap: "nowrap", minWidth: 0 }}>
          {r.top
            ? <GradeToken grade={r.top.grade} gv={r.top.grade_value} apex size="lg" />
            : <span className="gt gt-lg t1">—</span>}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>{r.top ? r.top.ascent : "no sends yet"}</div>
            <div className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.top ? `${fmtShort(r.top.climbed_on)}${r.top.venue ? ` · ${r.top.venue}` : ""}` : "log one to start the rule"}
            </div>
          </div>
        </div>
      </div>
      <div className="tnum" style={{ textAlign: "right", flexShrink: 0, lineHeight: 1.5 }}>
        <div style={{ fontSize: "var(--fs-sm)" }}><b>{r.sends}</b> <span className="muted">sends</span></div>
        <div style={{ fontSize: "var(--fs-sm)" }}><b>{r.goes}</b> <span className="muted">goes</span></div>
        <div style={{ fontSize: "var(--fs-sm)" }}><b>{r.days}</b> <span className="muted">day{r.days === 1 ? "" : "s"} out</span></div>
      </div>
    </div>
  );
}

function fmtShort(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** THE ASCENT PYRAMID — the home's lead. Sends distributed across grade bands, hardest at the
 *  apex, and every cell is an ascent-style MARK so you read HOW each one went, not just how many.
 *  An empty band is drawn as an open rule, never hidden — "the pyramid keeps its shape." */
export function Pyramid({ climbs, group }: { climbs: Climb[]; group: "boulder" | "rope" }) {
  const rows = useMemo(() => {
    const year = climbs.filter((c) => within(c, 365) && (group === "rope" ? isRope(c.discipline) : c.discipline === "boulder"));
    const byBand = new Map<string, { grade: string; gv: number; cells: Climb[]; open: boolean }>();
    for (const c of year) {
      const key = c.grade || "—";
      const e = byBand.get(key) ?? { grade: key, gv: c.grade_value, cells: [], open: false };
      if (c.sent) e.cells.push(c);
      else if (c.project) e.open = true;
      byBand.set(key, e);
    }
    return [...byBand.values()].sort((a, b) => b.gv - a.gv || b.grade.localeCompare(a.grade)).slice(0, 9);
  }, [climbs, group]);

  const apexGv = rows.reduce((m, r) => (r.cells.length ? Math.max(m, r.gv) : m), -1);

  if (!rows.length) {
    return <div className="proj-empty">Nothing in this band yet. Tap a grade below to put the first tick on the rule.</div>;
  }
  return (
    <>
      <div className="pyr">
        {rows.map((r) => (
          <div key={r.grade} className={`pyr-row${r.cells.length ? "" : " empty"}`}>
            <GradeToken grade={r.grade} gv={r.gv} apex={r.cells.length > 0 && r.gv === apexGv} />
            <div className="pyr-cells">
              {r.cells.slice(0, 14).map((c) => (
                <AscentMark key={c.id} ascent={(c.ascent as MarkId) || "redpoint"} sent title={`${c.grade} ${c.ascent}`} />
              ))}
              {r.open && !r.cells.length && (
                <span className="lbl" style={{ color: "var(--project)", letterSpacing: "0.12em" }}>open</span>
              )}
            </div>
            <span className="pyr-count">{r.cells.length}</span>
          </div>
        ))}
      </div>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>Sends by band · rolling 12 months</span>
        <span className="row" style={{ gap: 4, fontSize: "var(--fs-xs)", color: "var(--ink-3)" }}>
          <AscentMark ascent="attempt" sent={false} /> attempt
        </span>
      </div>
    </>
  );
}

/** PROJECTS — rebuilt from a thin strip into a real surface with the SESSION LADDER:
 *  one group of tally strokes per session, oldest left. This is the emotional core of the sport
 *  and the thing the route-database products handle worst; it is our differentiator. */
export function ProjectBoard({ signals, onLog }: { signals: SeasonSignals | null; onLog: () => void }) {
  const projects = signals?.projects ?? [];
  const open = projects.filter((p) => !p.sent).sort((a, b) => b.total_attempts - a.total_attempts);
  const gone = projects.filter((p) => p.sent).sort((a, b) => (b.last_touched > a.last_touched ? 1 : -1));

  const Row = ({ p, done }: { p: SeasonSignals["projects"][number]; done?: boolean }) => {
    const sessions = Math.max(1, p.sessions || 1);
    const per = Math.max(1, Math.round((p.total_attempts || sessions) / sessions));
    return (
      <div className={`proj-row${done ? " done" : ""}`}>
        <span className="proj-grade tnum">{p.grade || "—"}</span>
        <div style={{ minWidth: 0 }}>
          <div className="proj-where">{p.venue || p.discipline}</div>
          <div className="proj-ladder" aria-hidden="true">
            {Array.from({ length: Math.min(sessions, 6) }).map((_, s) => (
              <span className="proj-sess" key={s}>
                {Array.from({ length: Math.min(per, 7) }).map((__, i) => <i key={i} />)}
              </span>
            ))}
          </div>
        </div>
        <span className="proj-work tnum">
          {done ? "sent" : <>{p.total_attempts} goes<br />{sessions} session{sessions === 1 ? "" : "s"}</>}
        </span>
      </div>
    );
  };

  return (
    <section className="proj-board" aria-label="Your projects">
      <div className="proj-head">
        <span className="proj-title">{open.length ? `Working — ${open.length} open` : "Working"}</span>
        <button className="proj-add" onClick={onLog}>＋ Add</button>
      </div>
      {open.slice(0, 5).map((p, i) => <Row key={`o${i}`} p={p} />)}
      {open.length === 0 && (
        <div className="proj-empty">
          {/* "every project you have logged has gone" is nonsense to someone who has logged
              nothing — the two empty cases are different sentences. */}
          {gone.length
            ? "Nothing open. Every project you have logged has gone."
            : "A project is a climb you come back to. Mark one when you log it."}
        </div>
      )}
      {gone.length > 0 && (
        <div className="proj-sent">
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
            <span className="proj-sent-lbl">Gone</span>
            <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>last 90 days</span>
          </div>
          {gone.slice(0, 3).map((p, i) => <Row key={`s${i}`} p={p} done />)}
        </div>
      )}
    </section>
  );
}

/** SESSIONS — replaces "Recent ticks". History re-cut by the session it happened in, which is how
 *  climbers actually remember it, and how the leaders' logbooks read. No card, no chevron. */
export function Sessions({ climbs, onOpen }: { climbs: Climb[]; onOpen: (c: Climb) => void }) {
  const days = useMemo(() => {
    const m = new Map<string, Climb[]>();
    for (const c of climbs) (m.get(c.climbed_on) ?? m.set(c.climbed_on, []).get(c.climbed_on)!).push(c);
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0])).slice(0, 30);
  }, [climbs]);

  const spots = new Set(climbs.map((c) => c.venue).filter(Boolean)).size;

  if (!days.length) return null;
  return (
    <section style={{ marginTop: "var(--sp-5)" }}>
      <div className="proj-head" style={{ marginBottom: 4 }}>
        <span className="proj-title">Sessions</span>
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          {days.length} day{days.length === 1 ? "" : "s"} out · {spots} spot{spots === 1 ? "" : "s"}
        </span>
      </div>
      <div className="sess">
        {days.map(([date, list]) => {
          const d = new Date(date + "T00:00:00");
          const venue = list.find((c) => c.venue)?.venue || "";
          const partner = list.find((c) => c.partner)?.partner || "";
          return (
            // `border: 0` is load-bearing: a <button> inherits the UA's 1px border on all four
            // sides, which drew a white box around every session row. Same defect family as the
            // grade tokens — .sess-day carries ONLY the bottom graduation rule from styles.css.
            <button key={date} className="sess-day" onClick={() => onOpen(list[0])}
                    style={{ background: "none", border: 0, textAlign: "left", width: "100%",
                             cursor: "pointer", font: "inherit", color: "inherit" }}>
              <span className="sess-date">
                {d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}<br />
                {d.getDate()} {d.toLocaleDateString(undefined, { month: "short" }).toUpperCase()}
              </span>
              <span style={{ minWidth: 0 }}>
                <span className="sess-where row" style={{ gap: 6, flexWrap: "nowrap" }}>
                  {list[0] && <EnvMark env={list[0].environment} />}
                  {venue || list[0].discipline}
                </span>
                <span className="sess-run">
                  {list.slice(0, 8).map((c) => (
                    <span className="sess-tick" key={c.id}>
                      <AscentMark ascent={(c.ascent as MarkId) || "redpoint"} sent={c.sent} />
                      <b>{c.grade}</b>
                    </span>
                  ))}
                  <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
                    · {list.length} climb{list.length === 1 ? "" : "s"}{partner ? ` · ${partner}` : ""}
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginTop: "var(--sp-3)" }}>
        A tick is filed under the session it happened in. Tap a session to open it.
      </p>
    </section>
  );
}

/** FIRST RUN — ported from the delivery's "02 · States" board. This is what App Review sees
 *  first, and the designer art-directed it: the pyramid keeps its shape as open rules, and the
 *  scale question is asked once. Copy is the board's, verbatim. */
const SCALE_KEY = "oriora.sendlog.scale.v1";
export type Scale = "v" | "french" | "yds";
export const readScale = (): Scale => {
  try { return (localStorage.getItem(SCALE_KEY) as Scale) || "v"; } catch { return "v"; }
};
const writeScale = (s: Scale) => { try { localStorage.setItem(SCALE_KEY, s); } catch { /* ignore */ } };

export function FirstRun({ onPickScale }: { onPickScale: (s: Scale) => void }) {
  const scale = readScale();
  return (
    <section className="state" style={{ padding: "var(--sp-5) 0" }}>
      {/* the pyramid keeps its shape — empty bands are drawn as open rules, never hidden */}
      <div className="pyr" aria-hidden="true" style={{ marginBottom: "var(--sp-5)", opacity: 0.55 }}>
        {["V4", "V3", "V2", "V1", "V0"].map((g, i) => (
          <div className="pyr-row empty" key={g}>
            <span className={`gt t${Math.max(1, 2 - Math.floor(i / 2))}`}>{g}</span>
            <div className="pyr-cells" />
            <span className="pyr-count">0</span>
          </div>
        ))}
      </div>
      <h2>Nothing on the rule yet</h2>
      {/* §7b-verified: the delivery's line was "It never leaves this phone." That is an OVERCLAIM —
          the season recap does send derived figures. Privacy here means we do not RETAIN, not that
          nothing is ever transmitted. Reworded to what the app actually does. */}
      <p>One climb is enough to start the pyramid. Grade, where, how it went. Your climbs are
        stored on this device unless you back it up; only the season recap sends anything, and only the figures it needs.</p>

      <div className="lbl" style={{ marginBottom: "var(--sp-2)" }}>Which scale do you count in?</div>
      <div className="row" style={{ gap: "var(--sp-2)", marginBottom: "var(--sp-3)" }}>
        {([["v", "V-scale"], ["french", "French"], ["yds", "YDS"]] as [Scale, string][]).map(([k, label]) => (
          <button key={k} className={`chip${scale === k ? " selected" : ""}`}
                  onClick={() => { writeScale(k); onPickScale(k); }}>{label}</button>
        ))}
      </div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, maxWidth: "34ch" }}>
        Only sets the rail. You can type any grade you like — there is no route list to pick from,
        and that is the point.
      </p>
    </section>
  );
}

/** THE LOG RAIL — the app's own verb, pinned and reachable. Tap a band, the sheet opens with that
 *  grade already set. Replaces "+ Log climb" sitting below a tab switcher.
 *  The rail follows the scale the climber counts in; typing any other grade is always allowed. */
const SCALES: Record<Scale, { g: string; gv: number }[]> = {
  v: Array.from({ length: 10 }, (_, i) => ({ g: `V${i}`, gv: 100 + i * 5 })),
  french: ["4a", "4b", "4c", "5a", "5b", "5c", "6a", "6a+", "6b", "6c"].map((g, i) => ({ g, gv: 100 + i * 5 })),
  yds: ["5.6", "5.7", "5.8", "5.9", "5.10a", "5.10c", "5.11a", "5.11c", "5.12a", "5.12c"].map((g, i) => ({ g, gv: 100 + i * 5 })),
};

export function LogRail({ onLog, scale = "v", firstRun }: {
  onLog: (grade?: string) => void; scale?: Scale; firstRun?: boolean;
}) {
  return (
    <div className="log-rail">
      <div className="log-rail-lbl lbl">{firstRun ? "Tap a band to log your first climb" : "Tap a band to log"}</div>
      <div className="log-rail-row">
        <div className="log-rail-track">
          {SCALES[scale].map((b) => (
            <GradeToken key={b.g} grade={b.g} gv={b.gv} onClick={() => onLog(b.g)} title={`Log a ${b.g}`} />
          ))}
        </div>
        {/* Any grade at all — you can type it. There is no route list to pick from, and per the
            competitor study that is precisely our position: the leaders can only log against a
            route already in their database. */}
        <button className="log-rail-more" onClick={() => onLog()} aria-label="Log any other grade">
          &#8943;
        </button>
      </div>
    </div>
  );
}
